import { Elements, TextButton } from "@tfo/common"
import { useEffect, useState } from "react"
import { useIntl } from "react-intl"
import $http from "../../utils/$http"

import CircularProgress from "@material-ui/core/CircularProgress"
import "./BankIdSign.scss"
import { usePolling } from "./usePolling"

const { Heading4 } = Elements

export type BankIdSignProps = {
    orderId: number, 
    onCancel: () => void
    onComplete: () => void
    onError: () => void
}
 
type SignResponse = {
    qrCodeBase64Encoded: string
    qrStartTime: Date
}

type QrPromptProps = {
    qrCodeBase64: string
    qrStartTime: Date
}

type QrCodeResponse = {
    qrCodeBase64Encoded: string
}

type CollectResponse = {
    status: CollectStatus
    statusMessage: string
}

enum CollectStatus{
    Pending = "Pending",
    Complete = "Complete",
    Failure = "Failure",
    Retry = "Retry",
    Unknown = "Unknown",
}

const everySecond = 1000
const everyTwoSeconds = 2000

export const BankIdSign = ({ orderId, onCancel, onComplete, onError } : BankIdSignProps) => {
    const intl = useIntl()
    const [isStarting, setIsStarting] = useState<boolean>(true)
    const [qrCode, setQrCode] = useState<QrPromptProps | null>(null)
    const [statusMessage, setStatusMessage] = useState<string | null>(null)
    const [polling, setPolling] = useState<{qrInterval: number | null, collectInterval: number | null}>({qrInterval: null, collectInterval: null})

    const stopPolling = () => {
        setPolling({qrInterval: null, collectInterval: null})
    }
    const startPolling = () => {
        setPolling({qrInterval: everySecond, collectInterval: everyTwoSeconds})
    }

   usePolling(async () => {
        try {
            const { qrCodeBase64Encoded } = await $http.post<QrCodeResponse>(`bankid/${orderId}/qr-code`, { qrStartTime: qrCode?.qrStartTime })
            setQrCode((prev) => ({ ...prev, qrCodeBase64: qrCodeBase64Encoded }))
        } catch (error) {
            console.error("Failed to fetch QR code:", error)
        }
    }, polling.qrInterval)

   usePolling(async () => {
        try {
            const response = await $http.post<CollectResponse>(`bankid/${orderId}/collect`, { sameDevice: false })
          
            setStatusMessage(response.statusMessage)
            switch (response.status) {
                case CollectStatus.Complete: 
                    stopPolling()
                    onComplete()
                    break;
                case CollectStatus.Failure:
                    stopPolling()
                    onError()
                    break;
            }
        } catch (error) {
            console.error("Failed to collect status:", error)
        }
    }, polling.collectInterval)

    const cancelSigning = async () => {
        try {
            await $http.post(`bankid/${orderId}/cancel`, {})
        } catch (error) {
            console.error("Failed to cancel signing:", error)
        }
        finally{
            stopPolling()
            onCancel()
        }
    }

    useEffect(() => {
        const sameDevice = false //TODO: determine if same device or not
        const returnUrl = window.location.href + '?'

        const startSigning = async (orderId: number, sameDevice: boolean, returnUrl: string) => {
            try {
                const { qrCodeBase64Encoded, qrStartTime} = await $http.post<SignResponse>(`bankid/${orderId}/start`, { orderId, sameDevice, returnUrl })
                
                setQrCode({ qrCodeBase64: qrCodeBase64Encoded, qrStartTime })
                startPolling()
            } catch (error) {
                console.error("Failed to start signing:", error)
            } finally {
                setIsStarting(false)
            }
        }

        startSigning(orderId, sameDevice, returnUrl)
    }, [orderId])

    return (isStarting
        ? <div className='bankid-sign-container progressIcon'>
            <CircularProgress style={{ color: '#009B48' }} />
        </div>
        : <div className="bankid-sign-container">
            {statusMessage && <Heading4 >{statusMessage}</Heading4>}
            {qrCode && <QRPrompt {...qrCode } />}
            {onCancel && <TextButton
                secondary
                className=""
                onClick={cancelSigning}>
                {intl.formatMessage({
                    id: 'startIndividual.bankid.btn.cancel',
                })}
            </TextButton>}
        </div>)
}

export const QRPrompt: React.FC<QrPromptProps> = ({ qrCodeBase64 }) => {
    return (
        <>
            {qrCodeBase64 &&
                <div style={{display: 'flex', justifyContent: 'center'}} >
                    <img style={{width: '20rem', height: '20rem'}} src={'data:image/png;base64, ' + qrCodeBase64} alt="QR Code" ></img>
                </div>}
        </>
    )
}